import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import {
  Clock,
  LayoutDashboard,
  Users,
  Calendar,
  DollarSign,
  Building2,
  LogOut,
  User,
  ChevronDown,
  History,
  UserCog,
  CalendarOff,
  MessageSquare,
  HelpCircle,
  Key,
  Loader2,
  Globe,
  BarChart3,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_showtime-staff/artifacts/1ijkahsm_logo%20jf.png";

const Sidebar = () => {
  const { user, logout, selectedLocation, changeLocation, locations, getAccessibleLocations, canSeeAllLocations, isManager } = useAuth();
  const navigate = useNavigate();
  
  // Change password modal state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Profile modal state
  const [showProfile, setShowProfile] = useState(false);
  const [profileTimezone, setProfileTimezone] = useState(user?.timezone || '');
  const [profileTimeFormat, setProfileTimeFormat] = useState(user?.time_format || '12h');
  const [profileLoading, setProfileLoading] = useState(false);

  const US_TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST, no DST)' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleChangePassword = async () => {
    setPasswordError('');
    
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (passwordData.new.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    
    if (passwordData.new !== passwordData.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await authApi.changePassword(passwordData.current, passwordData.new);
      alert('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileLoading(true);
    try {
      await authApi.updateProfile({ timezone: profileTimezone, time_format: profileTimeFormat });
      alert('Profile updated successfully! Refresh to see all time displays update.');
      setShowProfile(false);
    } catch (err) {
      alert('Failed to update profile: ' + (err.message || 'Unknown error'));
    } finally {
      setProfileLoading(false);
    }
  };

  // Different nav items based on role
  const navItems = [
    // Admin & Manager items
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['admin', 'manager', 'accounting_admin', 'assistant_manager'] },
    { icon: Calendar, label: 'Schedule', path: '/schedule', roles: ['admin', 'manager', 'accounting_admin', 'assistant_manager'] },
    { icon: Clock, label: 'Time Entries', path: '/time-entries', roles: ['admin', 'manager', 'accounting_admin', 'assistant_manager'] },
    { icon: Users, label: 'Employees', path: '/employees', roles: ['admin', 'manager', 'accounting_admin'] },
    { icon: DollarSign, label: 'Payroll', path: '/payroll', roles: ['admin', 'manager', 'accounting_admin'] },  // Not for assistant_manager
    { icon: CalendarOff, label: 'Time Off Requests', path: '/time-off', roles: ['admin', 'manager', 'accounting_admin'] },
    { icon: MessageSquare, label: 'Messages', path: '/messages', roles: ['admin', 'manager', 'accounting_admin', 'assistant_manager'] },
    { icon: Building2, label: 'Locations', path: '/locations', roles: ['admin', 'accounting_admin'] },
    { icon: UserCog, label: 'User Management', path: '/users', roles: ['admin', 'accounting_admin'] },
    { icon: BarChart3, label: 'Reports', path: '/reports', roles: ['admin', 'accounting_admin', 'manager', 'assistant_manager'] },
    { icon: History, label: 'Audit Log', path: '/audit-log', roles: ['admin', 'accounting_admin'] },
    { icon: HelpCircle, label: 'Help', path: '/help', roles: ['admin', 'manager', 'accounting_admin', 'assistant_manager'] },
    
    // Employee items - only their own data
    { icon: Calendar, label: 'My Schedule', path: '/my-schedule', roles: ['employee'] },
    { icon: Clock, label: 'My Hours', path: '/my-hours', roles: ['employee'] },
    { icon: DollarSign, label: 'My Payroll', path: '/my-payroll', roles: ['employee'] },
    { icon: CalendarOff, label: 'Time Off', path: '/my-time-off', roles: ['employee'] },
    { icon: MessageSquare, label: 'Messages', path: '/my-messages', roles: ['employee'] },
    { icon: HelpCircle, label: 'Help', path: '/help', roles: ['employee'] },
  ];

  // Filter nav items based on user's roles (support multiple roles)
  const userRoles = user?.roles || [user?.role];
  const filteredNavItems = navItems.filter((item) => 
    item.roles.some(role => userRoles.includes(role))
  );

  // Get accessible locations using the context helper
  const accessibleLocations = getAccessibleLocations ? getAccessibleLocations() : (locations || []);

  return (
    <div className="w-64 bg-white border-r border-purple-100 h-screen flex flex-col shadow-sm">
      {/* Logo */}
      <div className="p-6 border-b border-purple-100">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="StaffEase Logo" className="w-10 h-10 object-contain" />
          <span className="text-xl font-bold text-purple-900">STP TeamBase</span>
        </div>
      </div>

      {/* Location Selector - Only for Admin/Manager/Accounting Admin/Assistant Manager */}
      {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'accounting_admin' || user?.role === 'assistant_manager' || 
        (user?.roles && user.roles.some(r => ['admin', 'manager', 'accounting_admin', 'assistant_manager'].includes(r)))) && (
        <div className="p-4 border-b border-purple-100">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">
            Location
          </label>
          <Select value={selectedLocation} onValueChange={changeLocation}>
            <SelectTrigger className="w-full bg-purple-50 border-purple-200 focus:ring-purple-500">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {canSeeAllLocations && canSeeAllLocations() && (
                <SelectItem value="all">All Locations</SelectItem>
              )}
              {accessibleLocations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isManager && isManager() && accessibleLocations.length > 1 && (
            <p className="text-xs text-gray-500 mt-1">
              {accessibleLocations.length} locations assigned
            </p>
          )}
        </div>
      )}

      {/* Employee Info Card */}
      {user?.role === 'employee' && (
        <div className="p-4 border-b border-purple-100">
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">Employee ID</p>
            <p className="font-mono font-semibold text-purple-900">{user.employeeNumber || user.employee_number}</p>
            <p className="text-xs text-gray-500 mt-2">Location</p>
            <p className="text-sm text-gray-700">
              {(locations || []).find(l => l.id === (user.locationId || user.location_id))?.name}
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-purple-100 text-purple-700 font-medium'
                      : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-purple-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3 px-3 hover:bg-purple-50">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatar} alt={user?.name} />
                <AvatarFallback className="bg-purple-100 text-purple-700">
                  {user?.name?.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => { setProfileTimezone(user?.timezone || ''); setProfileTimeFormat(user?.time_format || '12h'); setShowProfile(true); }} className="text-gray-600" data-testid="profile-menu-item">
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowChangePassword(true)} className="text-gray-600">
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Change Password Modal */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                placeholder="Enter current password"
                value={passwordData.current}
                onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={passwordData.new}
                onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                data-testid="confirm-password-input"
              />
            </div>
            {passwordError && (
              <p className="text-sm text-red-600">{passwordError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowChangePassword(false);
              setPasswordData({ current: '', new: '', confirm: '' });
              setPasswordError('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={passwordLoading}
              data-testid="change-password-submit-btn"
            >
              {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Profile Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium text-gray-900">{user?.name}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
              <p className="text-xs text-gray-400 capitalize mt-1">{user?.role?.replace('_', ' ')}</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-500" />
                Time Zone
              </Label>
              <Select value={profileTimezone} onValueChange={setProfileTimezone}>
                <SelectTrigger data-testid="profile-timezone-select">
                  <SelectValue placeholder="Select your timezone..." />
                </SelectTrigger>
                <SelectContent>
                  {US_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                This timezone will be used to display clock-in/out times for your location&apos;s employees.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                Time Format
              </Label>
              <Select value={profileTimeFormat} onValueChange={setProfileTimeFormat}>
                <SelectTrigger data-testid="profile-time-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">Standard (2:00 PM)</SelectItem>
                  <SelectItem value="24h">Military (14:00)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                How times appear across schedules, time entries, and reports.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfile(false)}>Cancel</Button>
            <Button
              onClick={handleSaveProfile}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={profileLoading}
              data-testid="save-profile-btn"
            >
              {profileLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sidebar;